import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Trash2, Scale, Cloud, Users } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from "recharts";
import { fetcher } from "@/lib/api";
import "@/i18n/fase9";

const WASTE_COLORS = {
  "Envases mezclados": "#F4A261",
  "Papel": "#0B4C7A",
  "Papel y cartón": "#0B4C7A",
  "Vidrio": "#10B981",
  "Envases de vidrio": "#10B981",
  "Resto": "#6B7280",
  "Fracción resto": "#6B7280",
  "Materia orgánica": "#7C4A28",
  "Orgánico": "#7C4A28",
  "Textil": "#E76F51",
  "sen_clasificar": "#9CA3AF",
  default: "#0B4C7A",
};

function color(name) {
  return WASTE_COLORS[name] || WASTE_COLORS.default;
}

// Format numbers in European locale (1.065 · 624,27)
function fmtInt(n) {
  return new Intl.NumberFormat("es-ES").format(Math.round(n || 0));
}
function fmtDec(n, d = 2) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n || 0);
}

export default function ImpactoRecogida() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data } = useSWR("/public/impacto/recogida", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 5 * 60 * 1000,
  });

  const totals = data?.totals || {};
  const byWaste = data?.distribution_by_waste_type || [];
  const byConcello = data?.distribution_by_concello || [];
  const donut = byWaste.map((r) => ({
    name: r.waste_type,
    value: Math.round(r.kg_total || 0),
  }));

  const totalKg = totals.residuos_totais_kg || 0;
  const totalT = totals.residuos_totais_t || 0;
  const intensity = totals.intensity_kgco2e_per_t;
  const perCapita = totals.residuos_per_capita_kg;
  const isEmpty = totalKg === 0;

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12" data-testid="impacto-recogida">
      {/* KPI tiles */}
      <div className="lg:col-span-5 grid grid-cols-2 gap-4">
        <StatTile
          icon={MapPin}
          label={t("fase9.contedores_impacto.total_col_points")}
          value="1.065"
          testid="impacto-sigeus-cp"
        />
        <StatTile
          icon={Trash2}
          label={t("fase9.contedores_impacto.total_containers")}
          value="2.094"
          accent="text-salnes-emerald"
          testid="impacto-sigeus-cont"
        />
        <StatTile
          icon={Scale}
          label={t("fase9.contedores_impacto.ytd_kg")}
          value={isEmpty ? "—" : fmtDec(totalT, 2)}
          suffix={isEmpty ? "" : "t"}
          testid="impacto-recogida-total-t"
        />
        <StatTile
          icon={Cloud}
          label={t("fase9.contedores_impacto.intensity")}
          value={intensity != null ? fmtDec(intensity, 1) : "—"}
          suffix={intensity != null ? "kg CO₂e/t" : ""}
          accent="text-salnes-coral"
          testid="impacto-recogida-intensity"
        />
        <StatTile
          icon={Users}
          label={t("fase9.contedores_impacto.per_capita")}
          value={perCapita != null ? fmtDec(perCapita, 1) : "—"}
          suffix={perCapita != null ? "kg/hab" : ""}
          testid="impacto-recogida-per-capita"
        />
        <StatTile
          icon={Trash2}
          label={t("fase9.contedores_impacto.num_recogidas")}
          value={isEmpty ? "—" : fmtInt(totals.num_recogidas)}
          accent="text-salnes-atlantic"
          testid="impacto-recogida-num-recogidas"
        />
      </div>

      {/* Donut + concello breakdown */}
      <div className="lg:col-span-7 space-y-4">
        <ChartCard
          title={t("fase9.contedores_impacto.by_waste_type")}
          testid="impacto-recogida-donut"
        >
          {donut.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={donut}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={105}
                  paddingAngle={2}
                >
                  {donut.map((d) => (
                    <Cell key={d.name} fill={color(d.name)} />
                  ))}
                </Pie>
                <ReTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  formatter={(v) => `${fmtInt(v)} kg`}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-10 text-center text-sm text-salnes-stone italic">
              {t("fase9.intensity.no_data_hint")}
            </div>
          )}
        </ChartCard>

        {byConcello.length > 0 && (
          <div className="rounded-2xl border hairline bg-white p-4" data-testid="impacto-recogida-concellos">
            <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
              {t("fase9.contedores_impacto.by_concello")}
            </div>
            <div className="space-y-1.5">
              {byConcello.slice(0, 9).map((c) => {
                const maxKg = byConcello[0]?.kg_total || 1;
                const pct = ((c.kg_total || 0) / maxKg) * 100;
                const slug = (c.municipality || "").toLowerCase().replace(/\s+/g, "-");
                return (
                  <div
                    key={c.municipality}
                    className="grid grid-cols-[minmax(120px,140px)_1fr_80px] items-center gap-3 text-xs"
                    data-testid={`impacto-recogida-concello-${slug}`}
                  >
                    <div className="truncate text-salnes-ink">{c.municipality}</div>
                    <div className="h-1.5 bg-salnes-sand/60 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-salnes-atlantic"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-right tabular-nums text-salnes-stone">
                      {fmtInt(c.kg_total)} kg
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-12">
        <p className="text-[10px] text-salnes-stone italic mt-1 text-right">
          {lang === "es"
            ? `Fuente: Operación (Valoriza) · Período: últimos ${data?.period?.days || 30} días · Actualizado cada 5 min`
            : `Fonte: Operación (Valoriza) · Período: últimos ${data?.period?.days || 30} días · Actualizado cada 5 min`}
        </p>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, suffix, accent = "text-salnes-atlantic", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl border hairline bg-white p-5 min-h-[118px] flex flex-col justify-between"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
        <Icon size={12} strokeWidth={1.6} className={accent} /> {label}
      </div>
      <div className="font-serif text-3xl text-salnes-ink leading-none tabular-nums flex items-baseline gap-2">
        {value ?? "—"}
        {suffix && (
          <span className="text-[10px] tracking-wider text-salnes-stone uppercase font-sans">
            {suffix}
          </span>
        )}
      </div>
    </motion.div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="rounded-2xl border hairline bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
